import { isDefined } from '@zorro/common-utils';
import { YesNo } from '@zorro/types';
import {
  AgentOrEmployee,
  CoveragePeriodFormFields,
  EnrollmentConfirmationFormFields,
  InitialPremiumProgress,
  PaymentStatusFormInput,
  PaymentStatusFormOutput,
  useCoveragePeriodForm,
  useEnrollmentConfirmationForm,
  useHydrateEnrollmentAllowanceStoreFromBenefits,
  usePaymentStatusForm,
} from '@zorro/web/shared-ui';
import {
  getEmployeeActions,
  useEmployerFeatureFlag,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { predictsAwaitingPayment } from './predictsAwaitingPayment';

type UseEnrollmentConfirmationFlowForm = (props: {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
}) => {
  enrollmentConfirmationForm: UseFormReturn<EnrollmentConfirmationFormFields>;
  paymentStatusForm: UseFormReturn<PaymentStatusFormInput>;
  validatePaymentStatus: () => Promise<PaymentStatusFormOutput | undefined>;
  isLoadingPaymentStatus: boolean;
  coveragePeriodForm: UseFormReturn<CoveragePeriodFormFields>;
  employerMonthlyContribution: number;
  requiredActions: string[];
  isSubmitEnabled: boolean;
  shouldHideCommunication: boolean;
};

export const useEnrollmentConfirmationFlowForm: UseEnrollmentConfirmationFlowForm =
  ({ employerId, employeeId, onboardingPeriodId, benefitId }) => {
    const { data: onboardingPeriod } = useMonolithQuery({
      method: 'onboardingPeriodsControllerFindOne',
      params: [employeeId, onboardingPeriodId],
    });

    const { data: benefits } = useMonolithQuery({
      method: 'benefitsControllerGetBenefits',
      params: [onboardingPeriodId],
    });

    const { data: enrollmentCommunicationConditions } = useMonolithQuery({
      method:
        'enrollmentCommunicationsControllerGetEnrollmentCommunicationConditions',
      params: onboardingPeriod?.id && [onboardingPeriod.id, employerId],
      enabled: isDefined(onboardingPeriod?.id),
    });

    const awaitingPaymentFlag = useEmployerFeatureFlag(
      'awaiting_payment_benefit_status_enabled',
      employerId
    );
    const isAwaitingPaymentFlagLoading =
      !awaitingPaymentFlag.isReady && !awaitingPaymentFlag.isError;

    const { enrollmentConfirmationForm, employerMonthlyContribution } =
      useEnrollmentConfirmationForm({ employeeId, onboardingPeriodId });

    const {
      paymentStatusForm,
      handleSubmit: handleSubmitPaymentStatus,
      isLoading: isLoadingBenefit,
      isValid: isPaymentStatusFormValid,
    } = usePaymentStatusForm(benefitId, true, false);

    const isLoadingPaymentStatus = isLoadingBenefit || !isDefined(benefitId);

    const validatePaymentStatus = async () => {
      let validValues: PaymentStatusFormOutput | undefined;
      await handleSubmitPaymentStatus((paymentStatusValues) => {
        validValues = paymentStatusValues;
      })();
      return validValues;
    };

    const { coveragePeriodForm } = useCoveragePeriodForm({
      employeeId,
      onboardingPeriodId,
      shouldShowLoadingOverlay: false,
    });

    const initialPremiumHandler = paymentStatusForm.watch(
      'initialPremiumHandler'
    );
    const initialPremiumCompleted = paymentStatusForm.watch(
      'initialPremiumCompleted'
    );
    const autoPayCompleted = paymentStatusForm.watch('autoPayCompleted');
    const autoPaySetter = paymentStatusForm.watch('autoPaySetter');

    const editedPremium = enrollmentConfirmationForm.watch('premium');

    useHydrateEnrollmentAllowanceStoreFromBenefits(
      benefits,
      benefitId,
      editedPremium
    );

    // Mirrors the rule the backend builds the email from: an employee who has
    // already set the payment up has no open action left.
    const conditionsWithPaymentInfo = useMemo(() => {
      if (!enrollmentCommunicationConditions) return undefined;
      return {
        ...enrollmentCommunicationConditions,
        isInitialPayments:
          initialPremiumHandler === AgentOrEmployee.EMPLOYEE &&
          initialPremiumCompleted === InitialPremiumProgress.NOT_SET_UP,
        isAutoPay:
          autoPaySetter === AgentOrEmployee.EMPLOYEE &&
          autoPayCompleted !== YesNo.YES,
      };
    }, [
      enrollmentCommunicationConditions,
      initialPremiumHandler,
      initialPremiumCompleted,
      autoPaySetter,
      autoPayCompleted,
    ]);

    const requiredActions = useMemo(
      () => getEmployeeActions(conditionsWithPaymentInfo, true),
      [conditionsWithPaymentInfo]
    );

    const majorMedicalPlan = benefits?.majorMedicalBenefit?.plan;
    const shouldHideCommunication =
      isAwaitingPaymentFlagLoading ||
      predictsAwaitingPayment({
        isAwaitingPaymentEnabled: awaitingPaymentFlag.isOn,
        premium: editedPremium ?? majorMedicalPlan?.premium,
        storedPremium: majorMedicalPlan?.premium,
        selfPayAmount: majorMedicalPlan?.selfPayAmount,
        isInitialPremiumCompleted:
          initialPremiumCompleted === InitialPremiumProgress.COMPLETED,
      });

    const isEnrollmentConfirmationFormValid =
      enrollmentConfirmationForm.formState.isValid;
    const isCoveragePeriodFormValid = coveragePeriodForm.formState.isValid;
    const isSubmitEnabled = Boolean(
      !isAwaitingPaymentFlagLoading &&
      isEnrollmentConfirmationFormValid &&
      isCoveragePeriodFormValid &&
      isPaymentStatusFormValid
    );
    return {
      enrollmentConfirmationForm,
      paymentStatusForm,
      validatePaymentStatus,
      isLoadingPaymentStatus,
      coveragePeriodForm,
      employerMonthlyContribution,
      requiredActions,
      isSubmitEnabled,
      shouldHideCommunication,
    };
  };
